import { z } from 'zod';
import { AffixListSchema } from '../data/affix-ids.js';
import { ItemSlotSchema, ITEM_KINDS } from '../data/items.js';
import { BIOME_IDS } from '../data/biomes.js';
import { AUCTION_MAX_PRICE } from '../rules/auction.js';
/**
 * The auction house (ADR 0035), over HTTP with a character session. A lot
 * is a snapshot of the stack it holds (template, quantity, smith's level)
 * at a fixed price; the server escrows the stack and moves the gold.
 */
export const AuctionListRequestSchema = z.object({
    itemId: z.uuid(),
    quantity: z.number().int().min(1).max(99),
    /** For the whole lot. */
    price: z.number().int().min(1).max(AUCTION_MAX_PRICE),
});
/** The room checks the live doorstep before granting a short HTTP ticket. */
export const AuctionAccessRequestSchema = z.object({ requestId: z.number().int().min(1) });
export const AuctionAccessEventSchema = z.discriminatedUnion('ok', [
    z.object({
        requestId: z.number().int().min(1),
        ok: z.literal(true),
        ticket: z.string().min(1),
        biome: z.enum(BIOME_IDS),
    }),
    z.object({ requestId: z.number().int().min(1), ok: z.literal(false) }),
]);
export const AUCTION_SORTS = ['recent', 'price', 'ending'];
/** The query string of a search; every field optional, numbers coerced. */
export const AuctionSearchQuerySchema = z.object({
    q: z.string().trim().max(40).optional(),
    slot: ItemSlotSchema.optional(),
    kind: z.enum(ITEM_KINDS).optional(),
    sort: z.enum(AUCTION_SORTS).default('recent'),
    page: z.coerce.number().int().min(0).max(1000).default(0),
});
/** `revoked`: the lot was a refunded shop purchase, taken back by the house (ADR 0044). */
export const AUCTION_STATUSES = [
    'active',
    'sold',
    'expired',
    'cancelled',
    'collected',
    'revoked',
];
export const AuctionStatusSchema = z.enum(AUCTION_STATUSES);
export const AuctionListingSchema = z.object({
    id: z.uuid(),
    sellerId: z.uuid(),
    sellerName: z.string().min(1),
    templateId: z.string().min(1),
    biome: z.enum(BIOME_IDS),
    quantity: z.number().int().min(1).max(99),
    upgradeLevel: z.number().int().min(0).max(10),
    /** The piece's affixes, which go with it to its buyer (ADR 0057). */
    affixes: AffixListSchema.optional(),
    price: z.number().int().min(1),
    status: AuctionStatusSchema,
    createdAt: z.string().min(1),
    expiresAt: z.string().min(1),
    /** Who bought it, once sold. */
    buyerName: z.string().min(1).nullable(),
});
export const AuctionSearchResponseSchema = z.object({
    listings: z.array(AuctionListingSchema),
    page: z.number().int().min(0),
    hasMore: z.boolean(),
});
/** My lots: the active ones and those waiting to be collected. */
export const AuctionMineResponseSchema = z.object({ listings: z.array(AuctionListingSchema) });
export const AuctionListingResponseSchema = z.object({ listing: AuctionListingSchema });
export const AUCTION_REFUSALS = [
    'unknown-item',
    /** A bound piece (ADR 0013) never goes up for sale. */
    'bound',
    /** A shop purchase still within its cooldown (ADR 0044). */
    'cooldown',
    'quantity',
    'too-many-listings',
    'unknown-listing',
    /** Sold, expired or withdrawn already. */
    'not-active',
    'own-listing',
    'missing-gold',
    'bag-full',
    'not-seller',
    /** Nothing to collect: still active, or already collected. */
    'not-collectable',
    /** A lot stays at the hotel where its seller deposited it. */
    'wrong-biome',
];
//# sourceMappingURL=auction.js.map